import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Divider,
  CircularProgress,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFoodcourt,
  createFoodcourt,
  updateFoodcourt,
  uploadFoodcourtImage,
} from 'apis/foodcourtAPIs';
import { getRestaurants } from 'apis/restaurantAPIs';
import { useDispatch } from 'react-redux';
import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 960,
  },
  backButton: {
    marginBottom: theme.spacing(2),
  },
  imageSection: {
    position: 'relative',
    width: '100%',
    height: 240,
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: `2px dashed ${theme.palette.grey[300]}`,
    cursor: 'pointer',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  uploadedImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  uploadPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: theme.palette.grey[500],
  },
  restaurantItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  qrSection: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  qrUrl: {
    wordBreak: 'break-all',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
}));

const Foodcourt = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { foodcourtId } = useParams();
  const fileInputRef = useRef(null);

  const isNew = foodcourtId === 'new';

  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [paymentPreferences, setPaymentPreferences] = useState({
    defaultPaymentMethod: '',
    requirePaymentOnOrder: false,
  });
  const [addRestaurantOpen, setAddRestaurantOpen] = useState(false);

  const { data: foodcourt, isLoading } = useQuery({
    queryKey: ['foodcourt', foodcourtId],
    queryFn: () => getFoodcourt(foodcourtId),
    enabled: !isNew && !!foodcourtId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: allRestaurants = [] } = useQuery({
    queryKey: ['restaurants'],
    queryFn: getRestaurants,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (foodcourt) {
      setName(foodcourt.name || '');
      setImageUrl(foodcourt.imageUrl || '');
      setSelectedRestaurants(foodcourt.restaurants || []);
      setPaymentPreferences(
        foodcourt.paymentPreferences || {
          defaultPaymentMethod: '',
          requirePaymentOnOrder: false,
        }
      );
    }
  }, [foodcourt]);

  const createMutation = useMutation({
    mutationFn: createFoodcourt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['foodcourts'] });
      dispatch(showSuccessSnackbar('Foodcourt created successfully'));
      if (imageFile && data.id) {
        handleImageUpload(data.id);
      } else {
        history.push('/foodcourts');
      }
    },
    onError: (err) => {
      dispatch(
        showErrorSnackbar(err.response?.data?.message || 'Failed to create foodcourt')
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateFoodcourt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foodcourts'] });
      queryClient.invalidateQueries({ queryKey: ['foodcourt', foodcourtId] });
      dispatch(showSuccessSnackbar('Foodcourt updated successfully'));
      if (imageFile) {
        handleImageUpload(foodcourtId);
      }
    },
    onError: (err) => {
      dispatch(
        showErrorSnackbar(err.response?.data?.message || 'Failed to update foodcourt')
      );
    },
  });

  const imageMutation = useMutation({
    mutationFn: uploadFoodcourtImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foodcourt', foodcourtId] });
      dispatch(showSuccessSnackbar('Image uploaded successfully'));
      if (isNew) {
        history.push('/foodcourts');
      }
    },
    onError: () => {
      dispatch(showErrorSnackbar('Failed to upload image'));
    },
  });

  const handleImageUpload = (id) => {
    if (!imageFile) return;
    const formData = new FormData();
    formData.append('image', imageFile);
    imageMutation.mutate({ foodcourtId: id, formData });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      dispatch(showErrorSnackbar('Please enter a foodcourt name'));
      return;
    }

    const data = {
      name: name.trim(),
      restaurants: selectedRestaurants.map((r) => ({
        id: r.id || r.urlIdentifier,
        name: r.name,
        urlIdentifier: r.urlIdentifier,
      })),
      paymentPreferences,
    };

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate({ foodcourtId, ...data });
    }
  };

  const handleAddRestaurant = (restaurant) => {
    if (!restaurant) return;
    const alreadyAdded = selectedRestaurants.some(
      (r) => (r.urlIdentifier || r.id) === (restaurant.urlIdentifier || restaurant.id)
    );
    if (alreadyAdded) {
      dispatch(showErrorSnackbar('Restaurant already added'));
      return;
    }
    setSelectedRestaurants([...selectedRestaurants, restaurant]);
    setAddRestaurantOpen(false);
  };

  const handleRemoveRestaurant = (index) => {
    setSelectedRestaurants(selectedRestaurants.filter((_, i) => i !== index));
  };

  const availableRestaurants = allRestaurants.filter(
    (r) =>
      !selectedRestaurants.some(
        (sr) => (sr.urlIdentifier || sr.id) === (r.urlIdentifier || r.id)
      )
  );

  const foodcourtPageUrl =
    foodcourtId && !isNew
      ? `${window.location.origin}/foodcourt-menu/${foodcourtId}`
      : null;

  const handleCopyUrl = () => {
    if (foodcourtPageUrl) {
      navigator.clipboard.writeText(foodcourtPageUrl);
      dispatch(showSuccessSnackbar('URL copied to clipboard'));
    }
  };

  const isSaving =
    createMutation.isPending || updateMutation.isPending || imageMutation.isPending;

  if (isLoading && !isNew) {
    return (
      <div className={classes.root}>
        <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 10 }} />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Button
        className={classes.backButton}
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/foodcourts')}
      >
        Back to Foodcourts
      </Button>

      <Typography variant="h4" gutterBottom>
        {isNew ? 'Create Foodcourt' : 'Edit Foodcourt'}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Basic Information" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Foodcourt Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Foodcourt Image
                  </Typography>
                  <div
                    className={classes.imageSection}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imagePreview || imageUrl ? (
                      <img
                        src={imagePreview || imageUrl}
                        alt="Foodcourt"
                        className={classes.uploadedImage}
                      />
                    ) : (
                      <div className={classes.uploadPlaceholder}>
                        <CloudUploadIcon sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body2">Click to upload image</Typography>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageSelect}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Restaurants"
              action={
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setAddRestaurantOpen(true)}
                >
                  Add Restaurant
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {selectedRestaurants.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <StorefrontIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography color="textSecondary">
                    No restaurants added yet
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {selectedRestaurants.map((restaurant, index) => (
                    <ListItem
                      key={restaurant.urlIdentifier || restaurant.id || index}
                      className={classes.restaurantItem}
                    >
                      <ListItemText
                        primary={restaurant.name}
                        secondary={restaurant.urlIdentifier || restaurant.id}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleRemoveRestaurant(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Payment Preferences" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Default Payment Method</InputLabel>
                    <Select
                      value={paymentPreferences.defaultPaymentMethod}
                      onChange={(e) =>
                        setPaymentPreferences({
                          ...paymentPreferences,
                          defaultPaymentMethod: e.target.value,
                        })
                      }
                      label="Default Payment Method"
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="stripe">Stripe</MenuItem>
                      <MenuItem value="adyen">Adyen</MenuItem>
                      <MenuItem value="wechat">WeChat Pay</MenuItem>
                      <MenuItem value="counter">Pay at Counter</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={paymentPreferences.requirePaymentOnOrder}
                        onChange={(e) =>
                          setPaymentPreferences({
                            ...paymentPreferences,
                            requirePaymentOnOrder: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Require payment before placing order"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {!isNew && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Foodcourt QR Code" />
              <Divider />
              <CardContent className={classes.qrSection}>
                <QrCode2Icon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Generate a QR code that links to a page showing all restaurants in this
                  foodcourt. Customers can scan individual restaurant QR codes to order.
                </Typography>
                {foodcourtPageUrl && (
                  <Paper variant="outlined" className={classes.qrUrl}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      <Typography variant="body2" color="textSecondary">
                        {foodcourtPageUrl}
                      </Typography>
                      <IconButton size="small" onClick={handleCopyUrl}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<QrCode2Icon />}
                  sx={{ mt: 3 }}
                  onClick={() => history.push(`/foodcourt-qr/${foodcourtId}`)}
                >
                  Generate QR Code
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => history.push('/foodcourts')}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <CircularProgress size={24} />
              ) : isNew ? (
                'Create Foodcourt'
              ) : (
                'Save Changes'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={addRestaurantOpen}
        onClose={() => setAddRestaurantOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Restaurant to Foodcourt</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Autocomplete
              options={availableRestaurants}
              getOptionLabel={(option) => option.name || ''}
              renderOption={(props, option) => (
                <li {...props} key={option.urlIdentifier || option.id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.urlIdentifier}
                    </Typography>
                  </Box>
                </li>
              )}
              onChange={(_, value) => handleAddRestaurant(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search restaurants"
                  variant="outlined"
                  autoFocus
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRestaurantOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Foodcourt;
